import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { DeliveryMode, ActiveCartFacade } from '@spartacus/cart/base/root';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, takeWhile, withLatestFrom } from 'rxjs/operators';
import { CheckoutDeliveryFacade } from '../../../root';
import { CheckoutConfigService } from '../../services/checkout-config.service';
import { CheckoutStepService } from '../../services/checkout-step.service';

declare module '@spartacus/cart/base/root' {
  export interface DeliveryMode {
    isClickAndCollectMode?: boolean;
    clickAndCollectLocations?: string[];
  }
}

@Component({
  selector: 'cx-delivery-mode',
  templateUrl: './delivery-mode.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeliveryModeComponent implements OnInit, OnDestroy {
  supportedDeliveryModes$!: Observable<DeliveryMode[]>;
  selectedDeliveryMode$!: Observable<DeliveryMode>;
  continueButtonPressed = false;

  backBtnText = this.checkoutStepService.getBackBntText(this.activatedRoute);

  deliveryModeSub!: Subscription;
  deliveryModes!: DeliveryMode[];

  collectLocationSub!: Subscription;
  collectLocation!: string;

  formMode: FormGroup = this.fb.group({
    deliveryModeId: ['', Validators.required],
  });

  constructor(
    private fb: FormBuilder,
    private checkoutDeliveryService: CheckoutDeliveryFacade,
    private checkoutConfigService: CheckoutConfigService,
    private activatedRoute: ActivatedRoute,
    protected checkoutStepService: CheckoutStepService,
    protected activeCartService: ActiveCartFacade
  ) {}

  ngOnInit() {
    this.checkoutDeliveryService.resetLoadSupportedDeliveryModesProcess();
    this.supportedDeliveryModes$ = this.checkoutDeliveryService.getSupportedDeliveryModes().pipe(
      filter((deliveryModes: DeliveryMode[]) => !!deliveryModes?.length),
      distinctUntilChanged((current: DeliveryMode[], previous: DeliveryMode[]) => {
        return JSON.stringify(current) === JSON.stringify(previous);
      })
    );

    // Reload delivery modes on error
    this.checkoutDeliveryService
      .getLoadSupportedDeliveryModeProcess()
      .pipe(takeWhile((state) => state?.success === false))
      .subscribe((state) => {
        if (state.error && !state.loading) {
          this.checkoutDeliveryService.loadSupportedDeliveryModes();
        }
      });

    this.deliveryModeSub = this.supportedDeliveryModes$
      .pipe(
        withLatestFrom(
          this.checkoutDeliveryService
            .getSelectedDeliveryMode()
            .pipe(map((deliveryMode: DeliveryMode | null | undefined) => deliveryMode?.code))
        )
      )
      .subscribe(
        ([
          deliveryModes,
          code,
        ]: [
          DeliveryMode[],
          (
            | string
            | undefined
          ),
        ]) => {
          this.deliveryModes = deliveryModes;
          deliveryModes.forEach((mode) => {
            this.formMode.addControl(mode.code + '-collectLocation', new FormControl(''));
            this.formMode.controls[mode.code + '-collectLocation'].disable();
          });

          if (!(code && !!deliveryModes.find((deliveryMode) => deliveryMode.code === code))) {
            code = this.checkoutConfigService.getPreferredDeliveryMode(deliveryModes);
          }
          if (code) {
            this.formMode.controls['deliveryModeId'].setValue(deliveryModes.find((mode) => mode.code === code));
            this.checkoutDeliveryService.setDeliveryMode(code);
            this.formMode.controls[code + '-collectLocation'].enable();
          }
        }
      );

    this.collectLocationSub = this.checkoutDeliveryService.loadCollectLocation().subscribe(() => {
      this.activeCartService.reloadActiveCart();
    });
  }

  onCollectLocationSelectionChange(location: string): void {
    this.checkoutDeliveryService.setCollectLocation(location);
  }

  changeMode(mode: DeliveryMode): void {
    this.deliveryModes.forEach((deliveryMode) => {
      if (deliveryMode.code === mode.code && deliveryMode.isClickAndCollectMode) {
        this.formMode.controls[deliveryMode.code + '-collectLocation'].enable();
      } else {
        if (deliveryMode.isClickAndCollectMode) {
          this.formMode.controls[deliveryMode.code + '-collectLocation'].disable();
          this.formMode.controls[deliveryMode.code + '-collectLocation'].setValue('');
          this.checkoutDeliveryService.clearCollectLocation();
        }
      }
    });

    if (mode.code) {
      this.checkoutDeliveryService.setDeliveryMode(mode.code);
    }
  }

  next(): void {
    if (this.formMode.valid && this.formMode.value) {
      this.continueButtonPressed = true;
      this.checkoutStepService.next(this.activatedRoute);
    }
  }

  back(): void {
    this.checkoutStepService.back(this.activatedRoute);
  }

  get deliveryModeInvalid(): boolean {
    return this.formMode.controls['deliveryModeId'].invalid || !this.isDeliveryModeValid();
  }

  isDeliveryModeValid(): boolean {
    return this.isClickAndCollectModeValid() || this.isStandardModeValid();
  }

  isClickAndCollectModeValid(): boolean {
    if (
      (this.formMode.get('deliveryModeId')?.value as DeliveryMode).isClickAndCollectMode &&
      (this.formMode.get((this.formMode.get('deliveryModeId')?.value as DeliveryMode).code + '-collectLocation')
        ?.value as string)
    ) {
      return true;
    }
    return false;
  }
  isStandardModeValid(): boolean {
    if (
      (this.formMode.get('deliveryModeId')?.value as DeliveryMode).isClickAndCollectMode === undefined ||
      !(this.formMode.get('deliveryModeId')?.value as DeliveryMode).isClickAndCollectMode
    ) {
      return true;
    }
    return false;
  }

  isSelected(mode: any): boolean {
    return this.formMode.get('deliveryModeId')?.value === mode;
  }

  ngOnDestroy(): void {
    if (this.deliveryModeSub) {
      this.deliveryModeSub.unsubscribe();
    }

    if (this.collectLocationSub) {
      this.collectLocationSub.unsubscribe();
    }
  }
}
